import {
  SHIP_CONNECTIONS,
  SHIP_SECTIONS,
  getSectionDefinition
} from "@wearecrew/shared";
import type { PlayerView, SectionView } from "../types";

interface ShipMapProps {
  players: PlayerView[];
  sections: SectionView[];
  localPlayerId: string;
  now: number;
  onMove: (sectionId: string) => void;
}

function center(sectionId: string): { x: number; y: number } {
  const section = getSectionDefinition(sectionId);
  return section
    ? { x: section.x + section.width / 2, y: section.y + section.height / 2 }
    : { x: 450, y: 280 };
}

function playerPosition(player: PlayerView, now: number): { x: number; y: number } {
  if (
    player.travelEndsAt <= now ||
    !player.travelFromSectionId ||
    !player.travelToSectionId
  ) {
    return center(player.sectionId);
  }

  const from = center(player.travelFromSectionId);
  const to = center(player.travelToSectionId);
  const duration = Math.max(1, player.travelEndsAt - player.travelStartedAt);
  const progress = Math.min(1, Math.max(0, (now - player.travelStartedAt) / duration));
  const eased = progress * progress * (3 - 2 * progress);
  return {
    x: from.x + (to.x - from.x) * eased,
    y: from.y + (to.y - from.y) * eased
  };
}

export function ShipMap({ players, sections, localPlayerId, now, onMove }: ShipMapProps) {
  const sectionState = new Map(sections.map((section) => [section.id, section]));
  const localPlayer = players.find((player) => player.id === localPlayerId);

  return (
    <div className="ship-map-wrap">
      <svg
        className="ship-map"
        viewBox="0 0 900 560"
        role="img"
        aria-label="Схема отсеков космического корабля"
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern id="grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(91,164,197,.07)" strokeWidth="1" />
          </pattern>
        </defs>

        <rect x="0" y="0" width="900" height="560" rx="26" className="map-background" />
        <rect x="0" y="0" width="900" height="560" rx="26" fill="url(#grid)" />

        <g className="connections">
          {SHIP_CONNECTIONS.map((connection) => {
            const from = center(connection.from);
            const to = center(connection.to);
            return (
              <g key={`${connection.from}-${connection.to}`}>
                <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} className="connection-shadow" />
                <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} className="connection-line" />
                <text x={(from.x + to.x) / 2} y={(from.y + to.y) / 2 - 8} className="travel-label">
                  {connection.travelSeconds}с
                </text>
              </g>
            );
          })}
        </g>

        <g className="sections">
          {SHIP_SECTIONS.map((section) => {
            const state = sectionState.get(section.id);
            const dangerous = (state?.fire ?? 0) > 4 || (state?.oxygen ?? 100) < 50 || (state?.pressure ?? 100) < 50;
            const isCurrent = localPlayer?.sectionId === section.id;
            return (
              <g
                key={section.id}
                className={`section ${dangerous ? "section-danger" : ""} ${isCurrent ? "section-current" : ""}`}
                onClick={() => onMove(section.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onMove(section.id);
                }}
              >
                <rect
                  x={section.x}
                  y={section.y}
                  width={section.width}
                  height={section.height}
                  rx="18"
                  className="section-body"
                  style={{ "--section-accent": section.accent } as React.CSSProperties}
                />
                <rect
                  x={section.x + 8}
                  y={section.y + 8}
                  width={section.width - 16}
                  height={section.height - 16}
                  rx="12"
                  className="section-inner"
                />
                <text x={section.x + 18} y={section.y + 30} className="section-name">
                  {section.shortName.toUpperCase()}
                </text>
                <text x={section.x + 18} y={section.y + section.height - 18} className="section-readout">
                  O₂ {Math.round(state?.oxygen ?? 100)}% · {Math.round(state?.pressure ?? 100)} kPa
                </text>
                {state && !state.powered && (
                  <text x={section.x + section.width - 20} y={section.y + 30} textAnchor="end" className="offline-label">
                    НЕТ ПИТАНИЯ
                  </text>
                )}
                {state && state.fire > 4 && (
                  <g className="hazard-badge" filter="url(#glow)">
                    <circle cx={section.x + section.width - 28} cy={section.y + section.height - 29} r="16" />
                    <text x={section.x + section.width - 28} y={section.y + section.height - 24} textAnchor="middle">
                      ▲
                    </text>
                  </g>
                )}
                {state && state.venting && (
                  <text x={section.x + section.width / 2} y={section.y + section.height / 2 + 7} textAnchor="middle" className="venting-label">
                    ОТКАЧКА ВОЗДУХА
                  </text>
                )}
              </g>
            );
          })}
        </g>

        <g className="players">
          {players.map((player, index) => {
            const position = playerPosition(player, now);
            const offset = (index % 4) * 7 - 10;
            const isLocal = player.id === localPlayerId;
            return (
              <g
                key={player.id}
                transform={`translate(${position.x + offset} ${position.y + offset})`}
                className={`player-marker ${isLocal ? "player-local" : ""} ${!player.connected ? "player-disconnected" : ""}`}
              >
                <circle r="17" fill={player.color} className="player-aura" />
                <circle r="11" fill={player.color} className="player-core" />
                <path d="M -5 -1 L 0 -6 L 5 -1 L 5 6 L -5 6 Z" className="player-silhouette" />
                {player.suitOn && <circle r="14" className="suit-ring" />}
                <text y="31" textAnchor="middle" className="player-name">
                  {isLocal ? `${player.name} · ВЫ` : player.name}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="map-hint">Кликните по отсеку, чтобы проложить маршрут</div>
    </div>
  );
}

